import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient, useAuth } from '../../providers/auth-provider';
import { useAgents } from '../../hooks/use-agents';
import { useAgentHistory } from '../../hooks/use-agent-history';
import { useAgentTree } from '../../hooks/use-agent-tree';
import { useRoles } from '../../hooks/use-roles';
import type { AgentDetailResponse, AgentSummaryResponse, RoleResponse } from '../../lib/api-client';
import { SkillFileBrowser } from '../skills/SkillFileBrowser';
import { SkillRevisionList } from '../skills/SkillRevisionList';
import { assignableRoles } from '../team/role-utils';

import { AgentsView, type AgentDetailData, type AgentSummary, type RoleOption } from './AgentsView';

/**
 * Top-level Agents admin container — owns the data wiring and renders the
 * designer-produced `AgentsView` with everything plumbed in. Mirrors the
 * shape of `TeamView` but lays out as a grid of cards (the design centerpiece).
 *
 * Gated upstream by the nav-item's `requireAnyCapability`; this view also
 * trusts the server's 403 if the user reaches it without `manage_agents`.
 */
export function AgentsViewContainer() {
  const apiClient = useApiClient();
  const { user } = useAuth();

  const {
    agents,
    status,
    error,
    filters: hookFilters,
    setFilters: setHookFilters,
    createAgent,
    deactivateAgent,
  } = useAgents();

  const { roles } = useRoles();

  // Selection + detail fetch.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AgentDetailResponse | null>(null);
  const [detailStatus, setDetailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [detailError, setDetailError] = useState<string | undefined>();

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailStatus('idle');
      setDetailError(undefined);
      return;
    }
    let cancelled = false;
    setDetailStatus('loading');
    apiClient
      .getAgent(selectedId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setDetailStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetailError(err instanceof Error ? err.message : 'Failed to load agent');
        setDetailStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, selectedId]);

  const onCreate = useCallback(
    async (input: {
      name: string;
      slug: string;
      description: string;
      role_id: string;
      include_soul_md: boolean;
    }) => {
      const created = await createAgent({
        name: input.name,
        slug: input.slug,
        description: input.description,
        role_id: input.role_id,
        include_soul: input.include_soul_md,
      });
      setSelectedId(created.id);
    },
    [createAgent],
  );

  const onDeactivate = useCallback(
    async (id: string) => {
      await deactivateAgent(id);
      if (selectedId === id) {
        setSelectedId(null);
      }
    },
    [deactivateAgent, selectedId],
  );

  // Role dropdown filtered by hierarchy (mirrors the user-invite flow).
  const actorCaps = user?.capabilities ?? {};
  const filteredRoles: RoleOption[] = useMemo(
    () =>
      assignableRoles(roles, actorCaps).map((r: RoleResponse) => ({
        id: r.id,
        slug: r.slug,
        name: r.name,
        capabilities: r.capabilities as Record<string, boolean>,
      })),
    [roles, actorCaps],
  );

  const canManage =
    actorCaps.manage_agents === true ||
    actorCaps.creator_mode === true ||
    actorCaps.full_access === true;

  // Marshal types — the AgentsView's internal `AgentSummary` matches the
  // response shape; the cast bridges the structural overlap.
  const agentSummaries: AgentSummary[] = agents as AgentSummary[];
  const detailData: AgentDetailData | null = detail as AgentDetailData | null;

  return (
    <AgentsView
      agents={agentSummaries}
      status={status}
      {...(error ? { error } : {})}
      filters={{
        search: hookFilters.search,
        roleId: hookFilters.roleId,
        status: hookFilters.status,
      }}
      setFilters={(next) =>
        setHookFilters({
          search: next.search,
          roleId: next.roleId,
          status: next.status,
        })
      }
      roles={filteredRoles}
      selectedAgentId={selectedId}
      selectedAgent={detailData}
      selectedAgentStatus={detailStatus}
      {...(detailError ? { selectedAgentError: detailError } : {})}
      onSelect={(id) => setSelectedId(id)}
      onCreate={onCreate}
      onDeactivate={onDeactivate}
      canManage={canManage}
      codeTabFor={(agent) => <AgentCodeTab agent={agent} />}
    />
  );
}

/**
 * Code & Revisions slot for the Agent detail pane. Pulls files + git history
 * via the same components Skills uses; the components are resource-agnostic.
 */
function AgentCodeTab({ agent }: { agent: AgentDetailData }) {
  const history = useAgentHistory(agent.id);
  const tree = useAgentTree(agent.id, history.viewedRef);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <SkillFileBrowser
        files={tree.files}
        filesStatus={tree.filesStatus}
        {...(tree.filesError !== undefined ? { filesError: tree.filesError } : {})}
        selectedPath={tree.selectedPath}
        onSelectFile={tree.selectFile}
        fileContent={tree.fileContent}
        fileStatus={tree.fileStatus}
        {...(tree.fileError !== undefined ? { fileError: tree.fileError } : {})}
        isAtRevision={history.viewedRef !== null}
        rootLabel={agent.name || agent.slug}
      />
      <SkillRevisionList
        commits={history.commits}
        status={history.status}
        {...(history.error !== undefined ? { error: history.error } : {})}
        hasMore={history.hasMore}
        onLoadMore={history.loadMore}
        viewedRef={history.viewedRef}
        onViewRef={history.viewAtRef}
      />
    </div>
  );
}

// Re-export for tests that want to render the inner view directly with mocked data.
// Keeps the wired container minimal at the registry level.
export { AgentsView };
// Silence unused-import warning for the summary shape on builds where TS strips it.
export type { AgentSummaryResponse };
